import { api } from './client';

export interface WikiSpace {
  id: string;
  name: string;
  slug: string;
  description: string | null;
  position: number;
  page_count: number;
  created_at: number;
  updated_at: number;
}

export interface WikiPageRef {
  id: string;
  space_id: string;
  title: string;
  slug: string;
  position: number;
  updated_by: string | null;
  created_at: number;
  updated_at: number;
}

export interface WikiPage extends WikiPageRef {
  body_md: string;
}

export interface WikiShareToken {
  token: string;
  space_id: string;
  label: string | null;
  created_by: string;
  created_at: number;
  revoked_at: number | null;
}

// ---- admin

export function listSpaces() {
  return api.get<WikiSpace[]>('/admin/wiki/spaces');
}

export function createSpace(input: { name: string; description?: string | null }) {
  return api.post<WikiSpace>('/admin/wiki/spaces', input);
}

export function updateSpace(id: string, input: { name?: string; description?: string | null }) {
  return api.patch<WikiSpace>(`/admin/wiki/spaces/${id}`, input);
}

export function deleteSpace(id: string) {
  return api.delete<{ id: string }>(`/admin/wiki/spaces/${id}`);
}

export function reorderSpaces(ids: string[]) {
  return api.post<{ ok: true }>('/admin/wiki/spaces/reorder', { ids });
}

export function listPages(spaceId: string) {
  return api.get<WikiPageRef[]>(`/admin/wiki/spaces/${spaceId}/pages`);
}

export function getPage(id: string) {
  return api.get<WikiPage>(`/admin/wiki/pages/${id}`);
}

export function createPage(spaceId: string, input: { title: string; body_md?: string }) {
  return api.post<WikiPage>(`/admin/wiki/spaces/${spaceId}/pages`, input);
}

export function updatePage(id: string, input: { title?: string; body_md?: string }) {
  return api.patch<WikiPage>(`/admin/wiki/pages/${id}`, input);
}

export function deletePage(id: string) {
  return api.delete<{ id: string }>(`/admin/wiki/pages/${id}`);
}

export function reorderPages(spaceId: string, ids: string[]) {
  return api.post<{ ok: true }>(`/admin/wiki/spaces/${spaceId}/pages/reorder`, { ids });
}

export function listShareTokens(spaceId: string) {
  return api.get<WikiShareToken[]>(`/admin/wiki/spaces/${spaceId}/tokens`);
}

export function createShareToken(spaceId: string, label?: string) {
  return api.post<WikiShareToken>(`/admin/wiki/spaces/${spaceId}/tokens`, { label });
}

export function revokeShareToken(token: string) {
  return api.delete<{ token: string }>(`/admin/wiki/tokens/${token}`);
}

// ---- public (no auth; the token in the path is the only credential)

export interface PublicWikiSpace {
  space: { name: string; description: string | null };
  pages: { id: string; title: string; slug: string }[];
}

export interface PublicWikiPage {
  id: string;
  title: string;
  slug: string;
  body_md: string;
  updated_at: number;
}

export function getPublicSpace(token: string) {
  return api.get<PublicWikiSpace>(`/wiki/s/${token}`, { skipAuth: true });
}

export function getPublicPage(token: string, slug: string) {
  return api.get<PublicWikiPage>(`/wiki/s/${token}/${slug}`, { skipAuth: true });
}
